import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Container, Paper, Box, ListItemText, Button, Divider, CircularProgress, Grid } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { fetchActiveUserAuctions } from '../services/auctions';
import { useNotification } from '../context/NotificationContext';
import PageLayout from '../components/PageLayout';
import { page } from '../theme/commonStyles';

/**
 * Rendert de "Mijn Veilingen" pagina.
 * Deze pagina haalt twee lijsten met veilingen op voor de ingelogde gebruiker en toont deze:
 * 1. Veilingen waaraan de gebruiker deelneemt.
 * 2. Veilingen die de gebruiker beheert als admin.
 * De pagina behandelt laad- en foutstatussen tijdens het ophalen van de data.
 * @returns {JSX.Element} Het gerenderde "Mijn Veilingen" pagina-component.
 */
function MyAuctions() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [managedAuctions, setManagedAuctions] = useState([]);
    const [participatingAuctions, setParticipatingAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const notify = useNotification();

    useEffect(() => {
        /**
         * Haalt de veilingen van de huidige gebruiker op, gesplitst in beheerd en
         * deelgenomen. Veilingen van een afgerond seizoen worden weggelaten.
         */
        const fetchAuctions = async () => {
            if (!user) return;
            setLoading(true);
            try {
                const { managed, participating } = await fetchActiveUserAuctions(user.uid);
                setManagedAuctions(managed);
                setParticipatingAuctions(participating);
            } catch (err) {
                notify.error('Fout bij het ophalen van uw veilingen.');
                console.error(err);
            }
            setLoading(false);
        };

        fetchAuctions();
    }, [user, notify]);

    if (loading) {
        return (
            <PageLayout>
                <Container sx={page.loadingContainer}><CircularProgress /></Container>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Container component="main" maxWidth="lg">
                <Box sx={{ mt: 4 }}>
                    <Typography component="h1" variant="h4" gutterBottom align="center">
                        Mijn Veilingen
                    </Typography>

                    {/* Deelnemende Veilingen */}
                    <Box sx={{ mb: 4 }}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            Mijn deelnames aan veilingen
                        </Typography>
                        {participatingAuctions.length === 0 ? (
                            <Typography sx={{ mt: 2 }}>Je neemt nog niet deel aan een veiling.</Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {participatingAuctions.map(auction => (
                                    <Grid item xs={12} sm={6} md={4} key={auction.id}>
                                        <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                                            <ListItemText primary={auction.name} secondary={auction.seasonName} />
                                            <Button variant="contained" onClick={() => navigate(`/auction/${auction.id}/my-team`)} sx={{ mt: 1 }}>
                                                Naar Veiling
                                            </Button>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* Beheerde Veilingen */}
                    <Box>
                        <Typography component="h2" variant="h5" gutterBottom>
                            Mijn veilingen in beheer
                        </Typography>
                        {managedAuctions.length === 0 ? (
                            <Typography sx={{ mt: 2 }}>Je beheert geen veilingen.</Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {managedAuctions.map(auction => (
                                    <Grid item xs={12} sm={6} md={4} key={auction.id}>
                                        <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                                            <ListItemText primary={auction.name} secondary={auction.seasonName} />
                                            <Button variant="contained" color="secondary" onClick={() => navigate(`/auction/${auction.id}/beheer/overzicht`)} sx={{ mt: 1 }}>
                                                Beheren
                                            </Button>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                </Box>
            </Container>
        </PageLayout>
    );
}

export default MyAuctions;
